import { useState, useRef, useEffect, type FormEvent, type KeyboardEvent, type ClipboardEvent } from "react";
import { Navigate } from "react-router-dom";
import { ArrowLeft, Phone, Moon, Sun, Loader2, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/context/AuthContext";
import { useThemeMode } from "@/theme/ThemeContext";
import FloatingIcons from "@/components/common/FloatingIcons";
import { sendOTP, verifyOTP } from "@/utils/auth-api";

const OTP_LENGTH = 6;

type Step = "phone" | "otp";

/* ─── 6-digit OTP input ─── */
function OTPInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
}) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Auto-focus first empty box
    const idx = value.length < OTP_LENGTH ? value.length : OTP_LENGTH - 1;
    inputRefs.current[idx]?.focus();
  }, []);

  const handleChange = (index: number, digit: string) => {
    if (!/^\d?$/.test(digit)) return;
    const arr = value.split("");
    arr[index] = digit;
    const next = arr.join("").replace(/[^\d]/g, "").slice(0, OTP_LENGTH);
    onChange(next);
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      const arr = value.split("");
      arr[index - 1] = "";
      onChange(arr.join(""));
    }
    if (e.key === "ArrowLeft" && index > 0) inputRefs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) inputRefs.current[index + 1]?.focus();
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    onChange(pasted);
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIdx]?.focus();
  };

  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length: OTP_LENGTH }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { inputRefs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          disabled={disabled}
          value={value[i] ?? ""}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          className={cn(
            "w-11 h-13 text-center text-lg font-semibold rounded-lg border border-input bg-transparent outline-none transition-all",
            "focus:border-foreground focus:ring-2 focus:ring-foreground/20",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        />
      ))}
    </div>
  );
}

/* ─── Login page ─── */
export default function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const { mode, toggleTheme } = useThemeMode();

  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const startCooldown = () => {
    setResendCooldown(30);
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOTP = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!phone.trim() || phone.length < 10) return;
    setLoading(true);
    setError("");
    try {
      const res = await sendOTP(phone.trim());
      if (res.status === "success") {
        setStep("otp");
        startCooldown();
      } else {
        setError("Failed to send OTP. Please try again.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e?: FormEvent) => {
    e?.preventDefault();
    if (otp.length < OTP_LENGTH) return;
    setLoading(true);
    setError("");
    try {
      const res = await verifyOTP(phone.trim(), otp.trim());
      if (res.status === "success") {
        login(res.auth_token, res.id);
      } else {
        setError("Invalid OTP. Please try again.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep("phone");
    setOtp("");
    setError("");
    setResendCooldown(0);
  };

  // Auto-submit when all 6 digits entered
  useEffect(() => {
    if (otp.length === OTP_LENGTH && step === "otp" && !loading) {
      handleVerifyOTP();
    }
  }, [otp]);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 relative overflow-hidden">
      <FloatingIcons />

      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="fixed top-5 right-5 z-10 text-muted-foreground"
      >
        {mode === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
      </Button>

      <div className="relative z-10 w-full max-w-[400px]">
        {/* Logo */}
        <div className="flex justify-center mb-10">
          <img
            src={mode === "light" ? "/pocoai-black.png" : "/pocoai-white.png"}
            alt="Pocoai"
            className="h-8 w-auto object-contain"
          />
        </div>

        {/* Shiny border card */}
        <div className="relative rounded-2xl p-px overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200%] aspect-square animate-[borderSpin_4s_linear_infinite] bg-[conic-gradient(from_0deg,transparent,transparent_30%,var(--foreground)_47%,var(--foreground)_53%,transparent_70%,transparent)]" />
          <div className="relative rounded-[15px] bg-card p-8">

            {step === "phone" ? (
              <>
                <h1 className="text-2xl font-semibold tracking-tight text-center">Welcome back</h1>
                <p className="text-sm text-muted-foreground mt-1.5 mb-8 text-center">
                  Enter your phone number to sign in
                </p>

                <form onSubmit={handleSendOTP} className="space-y-5">
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="phone"
                        placeholder="Enter your phone number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                        inputMode="numeric"
                        autoFocus
                        className="pl-9 h-11"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11"
                    disabled={loading || phone.length < 10}
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send OTP"}
                  </Button>
                </form>
              </>
            ) : (
              <>
                <button
                  onClick={goBack}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>

                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <ShieldCheck className="h-6 w-6 text-foreground" />
                  </div>
                </div>

                <h1 className="text-2xl font-semibold tracking-tight text-center">Verify OTP</h1>
                <p className="text-sm text-muted-foreground mt-1.5 mb-8 text-center">
                  Enter the 6-digit code sent to<br />
                  <span className="font-medium text-foreground">{phone}</span>
                </p>

                <form onSubmit={handleVerifyOTP} className="space-y-6">
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <OTPInput value={otp} onChange={setOtp} disabled={loading} />

                  <Button
                    type="submit"
                    className="w-full h-11"
                    disabled={loading || otp.length < OTP_LENGTH}
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Sign In"}
                  </Button>

                  <div className="text-center">
                    {resendCooldown > 0 ? (
                      <span className="text-sm text-muted-foreground">
                        Resend code in <span className="font-medium text-foreground">{resendCooldown}s</span>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleSendOTP()}
                        disabled={loading}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                      >
                        Didn't receive the code? <span className="font-medium underline underline-offset-2">Resend</span>
                      </button>
                    )}
                  </div>
                </form>
              </>
            )}

          </div>
        </div>

        <p className="text-xs text-muted-foreground/60 text-center mt-10">
          By signing in, you agree to our Terms of Service
        </p>
      </div>
    </div>
  );
}
